import { useState } from 'react'
import { AlertCircle, Check, Loader2, ShieldCheck } from 'lucide-react'
import { apiAdminRequestOtp, ApiRequestError, type OtpPayload } from '@/lib/api'
import type { OtpConfirmation } from '@nexbit/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'

/**
 * The two-step confirmation every OTP-protected admin action shares: ask for a code,
 * then submit the action with it.
 *
 * One component rather than the same twenty lines in each page, because the details
 * that make it safe are easy to get subtly different each time — the payload must be
 * the same object for both calls (the server fingerprints it, so a payload rebuilt
 * slightly differently is a code that will not verify), the code field must not be
 * submittable at the wrong length, and the failure message has to come back to the same
 * place whether the code was wrong, expired, or already used.
 *
 * What it deliberately does NOT do: hold the code anywhere but this component's state,
 * or log it. The code's whole value is that it lived only in the group chat and the
 * operator's head.
 */
export function OtpConfirmDialog({
  purpose,
  payload,
  title,
  description,
  confirmLabel,
  danger = false,
  onConfirm,
  onDone,
  onClose,
}: {
  purpose: string
  /** Must be exactly what the action will be submitted with — see OtpPayload. */
  payload: OtpPayload
  title: string
  /** One line telling the operator what they are approving, in their own words. */
  description: string
  confirmLabel: string
  /** Red confirm button for the ones that move money backwards. */
  danger?: boolean
  onConfirm: (otp: OtpConfirmation) => Promise<void>
  onDone: () => void
  onClose: () => void
}) {
  const [stage, setStage] = useState<'idle' | 'entering'>('idle')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [challengeId, setChallengeId] = useState<string | null>(null)

  async function requestCode() {
    setBusy(true)
    setErr(null)
    try {
      const issued = await apiAdminRequestOtp(purpose, payload)
      setChallengeId(issued.challengeId)
      setStage('entering')
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : 'ขอรหัสยืนยันไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  async function confirm() {
    if (!challengeId) return
    setBusy(true)
    setErr(null)
    try {
      await onConfirm({ challengeId, code })
      onDone()
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : 'ยืนยันไม่สำเร็จ')
      // The code is cleared on failure so a wrong one is not re-submitted by reflex
      // into the few attempts the challenge has left.
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!busy && !v) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">{title}</DialogTitle>
          <DialogDescription className="font-mono text-xs leading-relaxed">
            {stage === 'idle'
              ? description
              : 'รหัสถูกส่งเข้ากลุ่มเทเลแกรมของระบบแล้ว — กรอกรหัส 6 หลักเพื่อยืนยัน'}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="font-mono text-xs text-destructive">{err}</p>
          </div>
        )}

        {stage === 'idle' ? (
          <div className="rounded-[var(--radius-sm)] border border-border bg-secondary/40 p-2.5">
            <p className="font-mono text-[11px] leading-relaxed text-muted-foreground">
              รายการนี้ต้องใช้รหัสยืนยันจากกลุ่มเทเลแกรม ข้อความจะบอกชื่อผู้ขอ เวลาที่ขอ
              และรายการที่ทำ — ถ้าเห็นรายการที่ไม่ได้ขอเอง แจ้งทีมทันที
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="otp-code"
              className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
            >
              รหัสยืนยัน 6 หลัก
            </Label>
            <Input
              id="otp-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="h-10 text-center font-mono text-lg tracking-[0.4em]"
            />
            <p className="font-mono text-[10px] text-muted-foreground">
              ใช้ได้ 5 นาที · ใช้ได้ครั้งเดียว
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={stage === 'entering' ? () => { setStage('idle'); setCode(''); setErr(null) } : onClose}
            disabled={busy}
            className="h-8 font-mono text-xs"
          >
            {stage === 'entering' ? 'ย้อนกลับ' : 'ยกเลิก'}
          </Button>
          {stage === 'idle' ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void requestCode()}
              className="h-8 gap-1.5 font-mono text-xs"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
              {busy ? 'กำลังส่งรหัส...' : 'ขอรหัสยืนยัน'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant={danger ? 'destructive' : 'default'}
              disabled={code.length !== 6 || busy}
              onClick={() => void confirm()}
              className="h-8 gap-1.5 font-mono text-xs"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              {busy ? 'กำลังยืนยัน...' : confirmLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
